// 统一使用北京时间（UTC+8）计算"今天"，与后端 server/datetime.js 保持一致
// 此前用 toISOString() 取的是 UTC 日期，北京时间 0~8 点会落到"昨天"
export function beijingToday() {
  return new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().split('T')[0];
}

// ─── 解析后端返回的北京时间字符串 ──────────────────────────────────────────
// 后端所有时间字段格式均为 "YYYY-MM-DD HH:MM:SS"（北京时间 UTC+8）
// 直接 new Date(str) 会按【本地时区】解析，设备不在 UTC+8 时偏差 8 小时
// 正确做法：追加 "+08:00" 强制按 UTC+8 解析，getTime() 始终返回正确 UTC ms
export function parseBJ(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr.trim().replace(' ', 'T') + '+08:00');
}

// 相对时间（N分钟前/N小时前）
export function timeAgo(dateStr) {
  const d = parseBJ(dateStr);
  if (!d) return '';
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 5)     return '刚刚更新';
  if (diff < 60)    return `${diff} 秒前更新`;
  if (diff < 3600)  return `${Math.floor(diff / 60)} 分钟前更新`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} 小时前更新`;
  return `${Math.floor(diff / 86400)} 天前更新`;
}

// 格式化为 "YYYY-MM-DD HH:MM"，始终显示北京时间（无论用户设备时区）
export function formatBJ(dateStr, showSeconds = false) {
  const d = parseBJ(dateStr);
  if (!d) return '';
  return d.toLocaleString('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    ...(showSeconds ? { second: '2-digit' } : {}),
    hour12: false
  }).replace(/\//g, '-');
}

// 只取 HH:MM 部分
export function formatBJTime(dateStr) {
  const d = parseBJ(dateStr);
  if (!d) return '';
  return d.toLocaleTimeString('zh-CN', {
    timeZone: 'Asia/Shanghai',
    hour: '2-digit', minute: '2-digit', hour12: false
  });
}

// 北京时间当前分钟数（用于工作时间判断）
export function bjMinuteOfDay() {
  const bj = new Date(Date.now() + 8 * 60 * 60 * 1000);
  return bj.getUTCHours() * 60 + bj.getUTCMinutes();
}

// 文件名安全的时间戳："YYYY-MM-DD_HH-MM"（不含冒号/空格，可直接拼进文件名）
export function filenameTimestamp() {
  const bj = new Date(Date.now() + 8 * 60 * 60 * 1000);
  const pad = n => String(n).padStart(2, '0');
  return `${bj.getUTCFullYear()}-${pad(bj.getUTCMonth() + 1)}-${pad(bj.getUTCDate())}_${pad(bj.getUTCHours())}-${pad(bj.getUTCMinutes())}`;
}

// 根据数据入库时间（北京时间字符串 "YYYY-MM-DD HH:MM:SS"）计算72小时有效期截止时间
// 返回格式如 "2026-09-22 10:00"（北京时间，无秒）；传 null/'' 返回 null
export function calcExpiryStr(importedAt) {
  if (!importedAt) return null;
  const d = parseBJ(importedAt);
  if (!d || isNaN(d.getTime())) return null;
  const expiry = new Date(d.getTime() + 72 * 3600 * 1000);
  return expiry.toLocaleString('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    hour12: false
  }).replace(/\//g, '-');
}

// 从记录列表中取最早的入库时间字段，计算有效期截止时间
// importedAtKey 默认 'data_imported_at'；executeDispatch 返回的原始条目用 'imported_at'
export function calcExpiryFromRecords(records, importedAtKey = 'data_imported_at') {
  if (!records || !records.length) return null;
  const earliest = records
    .map(r => r[importedAtKey])
    .filter(Boolean)
    .sort()[0]; // "YYYY-MM-DD HH:MM:SS" 字典序即时间序
  return calcExpiryStr(earliest);
}

// 判断数据是否已过72小时有效期
// importedAt："YYYY-MM-DD HH:MM:SS"（北京时间），或 null
export function isDataExpired(importedAt) {
  if (!importedAt) return false;
  const d = parseBJ(importedAt);
  if (!d || isNaN(d.getTime())) return false;
  return Date.now() > d.getTime() + 72 * 3600 * 1000;
}

// 从记录列表中判断整批数据是否已过期（取最早入库时间）
export function isBatchExpired(records, importedAtKey = 'data_imported_at') {
  if (!records || !records.length) return false;
  const earliest = records.map(r => r[importedAtKey]).filter(Boolean).sort()[0];
  return isDataExpired(earliest);
}
